import { useState } from "react";
import { MessageSquare, Star, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const feedbackSchema = z.object({
  rating: z.number().min(1).max(5),
  type: z.string().min(1, "Please select a feedback type"),
  message: z.string().max(2000, "Message too long").optional(),
  email: z.string().email("Invalid email").max(255).optional(),
});

export const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedbackType, setFeedbackType] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const getRatingEmoji = (stars: number) => {
    if (stars <= 1) return "😞";
    if (stars === 2) return "😕";
    if (stars === 3) return "😐";
    if (stars === 4) return "😊";
    return "🤩";
  };

  const handleSubmit = async () => {
    try {
      // Validate input
      const validatedData = feedbackSchema.parse({
        rating,
        type: feedbackType,
        message: message.trim(),
        email: email.trim() || undefined,
      });

      setIsSubmitting(true);

      // Get current user if logged in
      const { data: { user } } = await supabase.auth.getUser();
      
      // If logged in and no email provided, use user's email
      let contactEmail = validatedData.email;
      if (!contactEmail && user?.email) {
        contactEmail = user.email;
      }

      // Construct message with rating
      const fullMessage = `Rating: ${validatedData.rating}/5 stars ${getRatingEmoji(validatedData.rating)}\n\n${validatedData.message || "No additional comments provided."}`;

      // Submit feedback to contact_messages table
      const { error } = await supabase
        .from("contact_messages")
        .insert({
          name: user?.email?.split("@")[0] || "Beta Tester",
          email: contactEmail || "beta-feedback@bitcoinmind.app",
          subject: `User Feedback - Beta (${validatedData.type})`,
          message: fullMessage,
          interest: validatedData.type,
          user_id: user?.id || null,
        });

      if (error) throw error;

      toast({
        title: "Thank you! 🙏",
        description: "Your feedback helps us improve BitcoinMind.",
      });

      // Reset form
      setRating(0);
      setFeedbackType("");
      setMessage("");
      setEmail("");
      setIsOpen(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.issues[0];
        toast({
          title: "Validation Error",
          description: firstError?.message || "Please check your input",
          variant: "destructive",
        });
      } else {
        console.error("Error submitting feedback:", error);
        toast({
          title: "Error",
          description: "Failed to submit feedback. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = rating > 0 && feedbackType;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          size="lg"
          className="fixed bottom-6 right-6 z-40 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 h-14 w-14 md:h-auto md:w-auto md:px-6"
          title="Give Feedback"
        >
          <MessageSquare className="h-5 w-5 md:mr-2" />
          <span className="hidden md:inline">Feedback</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="text-2xl">💭</span> Share Your Feedback
          </SheetTitle>
          <SheetDescription>
            We're in beta and your input matters! Help us build a better BitcoinMind.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Rating Selector */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">How would you rate your experience?</Label>
            <div className="flex items-center justify-center gap-2 py-4">
              {[1, 2, 3, 4, 5].map((stars) => (
                <button
                  key={stars}
                  type="button"
                  onClick={() => setRating(stars)}
                  onMouseEnter={() => setHoveredRating(stars)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                >
                  <Star
                    className={`h-8 w-8 ${
                      stars <= (hoveredRating || rating)
                        ? "fill-primary text-primary"
                        : "fill-muted text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center text-2xl animate-in fade-in duration-300">
                {getRatingEmoji(rating)}
              </p>
            )}
          </div>

          {/* Feedback Type */}
          <div className="space-y-2">
            <Label htmlFor="feedbackType" className="text-base font-semibold">
              What type of feedback do you have?
            </Label>
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger id="feedbackType">
                <SelectValue placeholder="Select feedback type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bug Report">🐛 Bug Report</SelectItem>
                <SelectItem value="Feature Request">✨ Feature Request</SelectItem>
                <SelectItem value="UI/UX Suggestion">🎨 UI/UX Suggestion</SelectItem>
                <SelectItem value="General Feedback">💬 General Feedback</SelectItem>
                <SelectItem value="Performance Issue">⚡ Performance Issue</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-base font-semibold">
              Tell us more <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="message"
              placeholder="Share your thoughts, suggestions, or describe any issues you've encountered..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={2000}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/2000 characters
            </p>
          </div>

          {/* Email (if not logged in) */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-semibold">
              Email <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
            />
            <p className="text-xs text-muted-foreground">
              We'll only use this to follow up on your feedback if needed.
            </p>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="w-full"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
